import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ShieldCheck, FileCheck2, Clock, GraduationCap, Lock, CreditCard, MessageCircle, Tag, BadgePercent, Paperclip, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient, formatApiError } from "@/lib/api";
import { Logo } from "@/components/landing/Logo";
import { Navbar } from "@/components/landing/Navbar";
import { SUPPORT_TYPES, CALC_SUBJECTS, COUNTRY_OPTIONS, FALLBACK_LEVELS, FALLBACK_DEADLINES, WHATSAPP_URL } from "@/components/landing/data";

const inputCls = "border-[#A07E1F]/25 bg-white text-[#12294D]";
const contentCls = "border-[#A07E1F]/25 bg-white text-[#12294D]";
const labelCls = "font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#5E6E85]";

const TRUST = [
  { icon: GraduationCap, text: "Degree-holding, subject-matched writers" },
  { icon: FileCheck2, text: "Free plagiarism & AI-detection report" },
  { icon: Clock, text: "On-time delivery — even 24-hour deadlines" },
  { icon: Lock, text: "Fully confidential. Card details never touch our servers" },
];

const PAGE_OPTIONS = Array.from({ length: 40 }, (_, i) => i + 1);

export const OrderPage = () => {
  const [rates, setRates] = useState({ levels: FALLBACK_LEVELS, deadlines: FALLBACK_DEADLINES, min_order: 29, currency: "£" });
  const [service, setService] = useState(SUPPORT_TYPES[0]);
  const [subject, setSubject] = useState(CALC_SUBJECTS[0]);
  const [level, setLevel] = useState("undergraduate");
  const [pages, setPages] = useState(4);
  const [deadline, setDeadline] = useState("7d");
  const [form, setForm] = useState({ name: "", email: "", phone: "", line1: "", city: "", state: "", country: "GB", postalCode: "", details: "" });
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles((prev) => {
      const combined = [...prev, ...incoming].slice(0, 2);
      if (prev.length + incoming.length > 2) toast.error("Maximum 2 documents allowed.");
      return combined;
    });
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  useEffect(() => {
    apiClient.get("/rates").then((r) => { if (r.data?.levels?.length) setRates(r.data); }).catch(() => {});
  }, []);

  const words = pages * 250;
  const { basePrice, price, per250, lvlLabel, dlLabel } = useMemo(() => {
    const lvl = rates.levels.find((l) => l.value === level) || rates.levels[0];
    const dl = rates.deadlines.find((d) => d.value === deadline) || rates.deadlines[0];
    const rate250 = lvl.rate250 * dl.multiplier;
    const base = Math.max(rates.min_order, Math.round(((words / 250) * rate250) * 100) / 100);
    const final = promo ? Math.round(base * (1 - promo.percent / 100) * 100) / 100 : base;
    return {
      basePrice: base,
      price: final,
      per250: rate250.toFixed(2),
      lvlLabel: lvl.label,
      dlLabel: dl.label,
    };
  }, [rates, level, deadline, words, promo]);

  const cur = rates.currency || "£";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await apiClient.get(`/promo/validate?code=${encodeURIComponent(promoInput.trim())}`);
      if (data.valid) {
        setPromo({ code: data.code, percent: data.percent });
        toast.success(`Code ${data.code} applied — ${data.percent}% off!`);
      } else {
        setPromo(null);
        toast.error("That code isn't valid or has expired.");
      }
    } catch {
      toast.error("Could not check the code. Try again.");
    } finally {
      setPromoChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      fd.append("service_type", service);
      fd.append("subject", subject);
      fd.append("academic_level", level);
      fd.append("deadline", deadline);
      fd.append("word_count", words);
      if (form.details) fd.append("details", form.details);
      if (promo?.code) fd.append("promo_code", promo.code);
      fd.append("line1", form.line1);
      fd.append("city", form.city);
      if (form.state) fd.append("state", form.state);
      fd.append("country", form.country);
      fd.append("postalCode", form.postalCode);
      files.forEach((f) => fd.append("files", f));
      const { data } = await apiClient.post("/checkout", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Order ${data.order_ref} created — redirecting to secure payment...`);
      window.location.assign(data.fwd_url);
    } catch (err) {
      toast.error(formatApiError(err, "Could not create your order. Please try again or message us on WhatsApp."));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F2]" data-testid="order-page">
      <Navbar />

      <main className="mx-auto max-w-7xl px-5 pb-12 pt-[132px] sm:px-8 sm:pb-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.33, 1, 0.68, 1] }}
            className="lg:col-span-2"
          >
            <p className="flex items-center gap-3 font-mono-accent text-[11px] uppercase tracking-[0.35em] text-[#A07E1F]">
              <span className="inline-block h-2 w-2 rotate-45 bg-[#D4AF37]" />
              Secure Checkout
            </p>
            <h1 className="mt-5 font-serif-display text-4xl font-medium leading-tight tracking-tight text-[#12294D] sm:text-5xl" data-testid="order-heading">
              Your order, <span className="italic text-[#A07E1F]">in safe hands.</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#46586F]">
              Tell us about your project, see your exact price instantly, and pay through our encrypted
              XPay checkout. Your expert begins the moment payment confirms.
            </p>
            <div className="mt-10 space-y-5">
              {TRUST.map((t) => (
                <p key={t.text} className="flex items-center gap-4 text-sm text-[#46586F]">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#A07E1F]/30 bg-[#D4AF37]/10">
                    <t.icon size={17} className="text-[#A07E1F]" />
                  </span>
                  {t.text}
                </p>
              ))}
            </div>
            <div className="mt-10 flex items-center gap-3 rounded-xl border border-[#A07E1F]/30 bg-[#D4AF37]/10 p-5" data-testid="order-promo-hint">
              <BadgePercent size={20} className="shrink-0 text-[#A07E1F]" />
              <p className="text-sm text-[#46586F]">
                First order? Use code <span className="font-mono-accent font-bold text-[#A07E1F]">WELCOME15</span> for 15% off.
              </p>
            </div>
            <a
              href={`${WHATSAPP_URL}?text=${encodeURIComponent("Hi! I have a question before placing my order.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-whatsapp-help"
              className="mt-8 inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#A07E1F] transition-colors hover:text-[#12294D]"
            >
              <MessageCircle size={15} /> Questions? Chat with us first
            </a>
          </motion.aside>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.33, 1, 0.68, 1] }}
            className="luxury-card rounded-xl p-6 sm:p-10 lg:col-span-3"
            data-testid="order-page-form"
          >
            <h2 className="font-serif-display text-2xl font-semibold text-[#12294D]">1. Your Project</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className={labelCls}>Service Type</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="order-service-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {SUPPORT_TYPES.map((s) => <SelectItem key={s} value={s} data-testid={`order-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Subject</Label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="order-subject-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {CALC_SUBJECTS.map((s) => <SelectItem key={s} value={s} data-testid={`order-subject-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Academic Level</Label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger data-testid="order-level-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {rates.levels.map((l) => <SelectItem key={l.value} value={l.value} data-testid={`order-level-${l.value}`}>{l.label} — {cur}{l.rate250}/250w</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Deadline</Label>
                <Select value={deadline} onValueChange={setDeadline}>
                  <SelectTrigger data-testid="order-deadline-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {rates.deadlines.map((d) => <SelectItem key={d.value} value={d.value} data-testid={`order-deadline-${d.value}`}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className={labelCls}>Length</Label>
                <Select value={String(pages)} onValueChange={(v) => setPages(Number(v))}>
                  <SelectTrigger data-testid="order-pages-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {PAGE_OPTIONS.map((p) => <SelectItem key={p} value={String(p)} data-testid={`order-pages-${p}`}>{p} {p === 1 ? "page" : "pages"} / {p * 250} words</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-[#A07E1F]/35 bg-[#F3EEE2] p-6" data-testid="order-price-panel">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#5E6E85]">Your exact price</p>
                  <div className="mt-1 flex items-end gap-3">
                    {promo && <span className="pb-1 font-serif-display text-2xl text-[#75849B] line-through" data-testid="order-price-original">{cur}{basePrice}</span>}
                    <p className="font-serif-display text-5xl font-semibold text-[#A07E1F]" data-testid="order-price-value">{cur}{price}</p>
                  </div>
                  <p className="mt-1 text-xs text-[#5E6E85]" data-testid="order-price-breakdown">
                    {words.toLocaleString()} words · {lvlLabel} · {dlLabel} · {cur}{per250}/250 words
                    {promo && <span className="ml-1 font-semibold text-[#A07E1F]" data-testid="order-promo-applied">· {promo.code} applied (−{promo.percent}%)</span>}
                  </p>
                </div>
                <p className="flex max-w-[220px] items-start gap-2 text-xs text-[#46586F]">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#A07E1F]" />
                  Includes free originality report, referencing in any style and 14 days of free revisions.
                </p>
              </div>
              <div className="mt-5 flex gap-2 border-t border-[#A07E1F]/20 pt-5">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A07E1F]" />
                  <Input
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    placeholder="Promo code (e.g. WELCOME15)"
                    data-testid="order-promo-input"
                    className={`${inputCls} pl-10 font-mono-accent uppercase tracking-[0.1em]`}
                  />
                </div>
                <button
                  type="button"
                  onClick={applyPromo}
                  disabled={promoChecking}
                  data-testid="order-promo-apply"
                  className="rounded-full border border-[#A07E1F]/50 px-6 font-mono-accent text-[11px] font-bold uppercase tracking-[0.15em] text-[#A07E1F] transition-colors hover:bg-[#D4AF37]/15 disabled:opacity-60"
                >
                  {promoChecking ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
                </button>
              </div>
            </div>

            <h2 className="mt-10 font-serif-display text-2xl font-semibold text-[#12294D]">2. Your Details</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="op-name" className={labelCls}>Full Name *</Label>
                <Input id="op-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="order-name-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-email" className={labelCls}>Email *</Label>
                <Input id="op-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="order-email-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-phone" className={labelCls}>Phone / WhatsApp</Label>
                <Input id="op-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="order-phone-input" placeholder="+44 ..." className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-line1" className={labelCls}>Address *</Label>
                <Input id="op-line1" required value={form.line1} onChange={(e) => set("line1", e.target.value)} data-testid="order-address-input" placeholder="Street address" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-city" className={labelCls}>City *</Label>
                <Input id="op-city" required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="order-city-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Country *</Label>
                <Select value={form.country} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="order-country-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className={contentCls}>
                    {COUNTRY_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`order-country-${c.code.toLowerCase()}`}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-state" className={labelCls}>State / Region</Label>
                <Input id="op-state" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="order-state-input" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="op-postal" className={labelCls}>Postcode *</Label>
                <Input id="op-postal" required value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} data-testid="order-postal-input" className={inputCls} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="op-details" className={labelCls}>Project Details</Label>
                <Textarea id="op-details" rows={4} value={form.details} onChange={(e) => set("details", e.target.value)} data-testid="order-details-textarea" placeholder="Topic, referencing style, marking criteria, anything your writer should know..." className={inputCls} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="op-files" className={labelCls}>Attach Assignment Brief (optional — up to 2 documents)</Label>
                <label
                  htmlFor="op-files"
                  data-testid="order-files-dropzone"
                  className="flex cursor-pointer items-center justify-center gap-3 rounded-lg border border-dashed border-[#A07E1F]/40 bg-[#FAF8F2] px-6 py-7 text-sm text-[#46586F] transition-colors duration-300 hover:border-[#A07E1F] hover:text-[#12294D]"
                >
                  <Paperclip size={17} className="text-[#A07E1F]" />
                  Attach your brief — DOCX, PDF, JPG, JPEG or PNG (max 15 MB each)
                </label>
                <input
                  id="op-files"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                  data-testid="order-files-input"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="order-files-list">
                    {files.map((f, i) => (
                      <span key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-full border border-[#A07E1F]/30 bg-white px-3.5 py-1.5 text-xs text-[#12294D]" data-testid={`order-file-chip-${i}`}>
                        <FileCheck2 size={13} className="text-[#A07E1F]" />
                        {f.name}
                        <button type="button" onClick={() => removeFile(i)} data-testid={`order-file-remove-${i}`} className="text-[#75849B] transition-colors hover:text-red-500" aria-label={`Remove ${f.name}`}>
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="order-pay-button"
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] py-4 font-mono-accent text-xs font-bold uppercase tracking-[0.2em] text-[#12294D] transition-colors duration-300 hover:bg-[#C9A227] disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {loading ? "Creating secure checkout..." : `Pay ${cur}${price} Securely`}
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-[#75849B]">
              <Lock size={12} className="text-[#A07E1F]" /> Encrypted XPay checkout · We never see or store your card details
            </p>
          </motion.form>
        </div>
      </main>
    </div>
  );
};
